import app from 'flarum/forum/app';
import extractText from 'flarum/common/utils/extractText';

/**
 * Reglas y catálogo de M6 que no son pintado.
 *
 * Tercer gemelo de `registerForm.ts` / `requestForm.ts`, y por la misma razón: la validación de
 * cliente se lee de un vistazo junto a la de servidor (`src/Command/ProposeMeetupHandler.php`).
 * El cliente valida por UX —error inline al enviar, foco al primero— y el servidor porque es el
 * único que manda; cuando discrepan gana el servidor y el formulario lo muestra igual.
 */

/** Campos, en el orden en que aparecen (el foco al primer error lo usa). */
export const FIELD_ORDER = ['topic', 'format', 'place', 'speaker', 'reason'] as const;

export type FieldName = (typeof FIELD_ORDER)[number];
export type Errors = Partial<Record<FieldName, string>>;

/**
 * `place` no entra aquí desde T-FORO-V14: es texto libre (comuna + lugar concreto), no un
 * catálogo de opciones fijas. Sólo `format` sigue siendo un desplegable.
 */
export type MeetupOptions = {
  format: string[];
};

export type MeetupLimits = {
  topicMin: number;
  topicMax: number;
  placeMin: number;
  placeMax: number;
  speakerMax: number;
  reasonMin: number;
  reasonMax: number;
  interestThreshold: number;
};

/**
 * Los límites llegan del servidor (`Api\AddMeetupOptions`) para que los mensajes los interpolen
 * en vez de repetir el número. El respaldo espeja `Profile\MeetupOptions` y sólo entra en juego
 * si el payload viniera sin el atributo — un foro a medio desplegar, por ejemplo. Sin respaldo,
 * `undefined` en un `min:` dejaría pasar cualquier cosa al servidor y el error llegaría tarde.
 */
const FALLBACK_LIMITS: MeetupLimits = {
  topicMin: 8,
  topicMax: 110,
  placeMin: 3,
  placeMax: 140,
  speakerMax: 120,
  reasonMin: 20,
  reasonMax: 600,
  interestThreshold: 15,
};

export function meetupOptions(): MeetupOptions {
  const raw = app.forum.attribute<Partial<MeetupOptions>>('umbralComunidadMeetupOptions') || {};

  return { format: raw.format || [] };
}

export function meetupLimits(): MeetupLimits {
  const raw = app.forum.attribute<Partial<MeetupLimits>>('umbralComunidadMeetupLimits') || {};

  return { ...FALLBACK_LIMITS, ...raw };
}

export function proposePath(): string {
  return app.route('umbral-comunidad.proponer');
}

export type FormValues = {
  topic: string;
  format: string;
  place: string;
  speaker: string;
  reason: string;
};

export function emptyValues(): FormValues {
  return { topic: '', format: '', place: '', speaker: '', reason: '' };
}

const ERRORS = 'umbral-comunidad.forum.proponer.errors';

/** Validación de cliente. Espeja la del servidor. */
export function validate(values: FormValues, limits: MeetupLimits): Errors {
  const errors: Errors = {};

  const topic = values.topic.trim();
  if (!topic) errors.topic = extract(`${ERRORS}.topic_required`);
  else if (topic.length < limits.topicMin) errors.topic = interpolate('topic_short', { min: limits.topicMin });
  else if (topic.length > limits.topicMax) errors.topic = interpolate('topic_long', { max: limits.topicMax });

  if (!values.format) errors.format = extract(`${ERRORS}.format`);

  // TEXTO LIBRE desde T-FORO-V14 (antes `!values.place` bastaba porque era un `<select>`).
  const place = values.place.trim();
  if (!place) errors.place = extract(`${ERRORS}.place_required`);
  else if (place.length < limits.placeMin) errors.place = interpolate('place_short', { min: limits.placeMin });
  else if (place.length > limits.placeMax) errors.place = interpolate('place_long', { max: limits.placeMax });

  if (values.speaker.trim().length > limits.speakerMax) {
    errors.speaker = interpolate('speaker_long', { max: limits.speakerMax });
  }

  const reason = values.reason.trim();
  if (!reason) errors.reason = extract(`${ERRORS}.reason_required`);
  else if (reason.length < limits.reasonMin) errors.reason = interpolate('reason_short', { min: limits.reasonMin });
  else if (reason.length > limits.reasonMax) errors.reason = interpolate('reason_long', { max: limits.reasonMax });

  return errors;
}

/**
 * Punteros JSON:API -> campos. El handler valida los atributos en plano, así que llegan como
 * `/data/attributes/<campo>`. Lo que no se reconozca sale entero en la barra de arriba.
 */
export function mapServerErrors(response: any): { errors: Errors; unmapped: string[] } {
  const errors: Errors = {};
  const unmapped: string[] = [];
  const list: any[] = (response && response.errors) || [];
  const known = new Set<string>([...FIELD_ORDER]);

  list.forEach((error) => {
    const pointer: string = (error && error.source && error.source.pointer) || '';
    const detail: string = (error && error.detail) || '';
    const field = pointer.replace('/data/attributes/', '').split('.')[0];

    if (field && known.has(field)) errors[field as FieldName] = detail || extract(`${ERRORS}.generic`);
    else if (detail) unmapped.push(detail);
  });

  return { errors, unmapped };
}

/**
 * Los conflictos de estado (409) que puede devolver `POST /umbral/meetup-requests`.
 *
 * TERCER GEMELO, y llega tarde a proposito de nada: `requestForm.ts` tenia esta funcion desde
 * T08 y aqui no existia, asi que M6 no sabia traducir NINGUN codigo — el 429 se interceptaba a
 * mano en `ProponerPage.onerror` y el 409 de correo sin confirmar caia entero en la alerta
 * generica de Flarum. Los dos codigos que el servidor emite de verdad hoy estan aqui:
 *
 *   `umbral_email_unconfirmed`  `ProposeMeetupHandler.php` — segunda puerta, antes de validar.
 *   `umbral_rate_limited`       `Security\SensitiveRouteRateLimiter` — ruta meetup-requests.
 *
 * Devuelve null si el error no es uno de esos: ahi manda `mapServerErrors` (errores de campo).
 */
export function conflictMessage(response: any): string | null {
  const code: string = (response && response.errors && response.errors[0] && response.errors[0].code) || '';

  const known: Record<string, string> = {
    umbral_email_unconfirmed: 'email_unconfirmed',
    umbral_rate_limited: 'rate_limited',
  };

  return known[code] ? extract(`${ERRORS}.${known[code]}`) : null;
}

/** Gemelo de `requestForm.isEmailUnconfirmedConflict`; ver alli por que va aparte del mensaje. */
export function isEmailUnconfirmedConflict(response: any): boolean {
  const code: string = (response && response.errors && response.errors[0] && response.errors[0].code) || '';

  return code === 'umbral_email_unconfirmed';
}

function interpolate(key: string, params: Record<string, number>): string {
  return extractText(app.translator.trans(`${ERRORS}.${key}`, params as any));
}

function extract(key: string): string {
  return extractText(app.translator.trans(key));
}
